import {
  OAuthProvider,
  signInWithPopup,
  updateProfile,
  deleteUser,
  User,
  getAdditionalUserInfo,
} from "firebase/auth";
import {
  initFirebase,
  clearFirebaseLocalStorage,
} from "@core/services/api/firebase";
import { apiRequest } from "@core/services/api/apiRequest";
import type { ClaimedVia } from "@core/features/invitation";

export async function registerUserWithOAuth(
  provider: OAuthProvider,
  inviteCode: string | null,
  claimedVia?: ClaimedVia | null,
  onError?: () => void
) {
  let firebaseUser: User | null = null;
  let isNewUser = false;

  try {
    // Ensure firebase is initialized
    const auth = await initFirebase();

    // Sign in or register user with OAuth provider
    const result = await signInWithPopup(auth, provider);
    firebaseUser = result.user;

    // Check if this is a new user (just created) vs existing user signing in
    const additionalInfo = getAdditionalUserInfo(result);
    isNewUser = additionalInfo?.isNewUser ?? false;

    // Get user's name from the provider's data
    const firstName = firebaseUser.displayName?.split(" ")[0] || "";
    const lastName =
      firebaseUser.displayName?.split(" ").slice(1).join(" ") || "";

    // Update user profile with the parsed name
    await updateProfile(firebaseUser, {
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
    });

    // Get Firebase token
    const idToken = await firebaseUser.getIdToken();

    // Prepare API request body
    const body = {
      firebase_token: idToken,
      email: firebaseUser.email,
      first_name: firstName,
      last_name: lastName,
      ...(inviteCode && { invite_code: inviteCode }),
      ...(claimedVia && { claimed_via: claimedVia }),
    };

    // Make API call to backend
    const response = await apiRequest("/invitation/signup/", "POST", body);

    return { authToken: idToken, ...response };
  } catch (error) {
    // Only clean up Firebase user if it was just created in this request
    if (firebaseUser && isNewUser) {
      try {
        await deleteUser(firebaseUser);
      } catch (deleteError) {
        console.error("Failed to clean up Firebase user:", deleteError);
      }
    }

    clearFirebaseLocalStorage();

    onError?.();

    throw error;
  }
}
